import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  message,
  Typography,
  Tag,
  Tabs,
  DatePicker,
  Button,
  Space,
} from 'antd';
import {
  DollarOutlined,
  CreditCardOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);
  const [activeTab, setActiveTab] = useState('resumen');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      let url = '/dashboard';
      const params = new URLSearchParams();
      
      if (fechaDesde) {
        params.append('fechaDesde', fechaDesde.format('YYYY-MM-DD'));
      }
      if (fechaHasta) {
        params.append('fechaHasta', fechaHasta.format('YYYY-MM-DD'));
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await apiClient.get(url);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los datos del dashboard');
      }
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      message.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFechaChange = (dates) => {
    if (dates && dates.length === 2) {
      setFechaDesde(dates[0]);
      setFechaHasta(dates[1]);
    } else {
      setFechaDesde(null);
      setFechaHasta(null);
    }
  };

  const handleAplicarFiltros = () => {
    loadDashboardData();
  };

  const handleLimpiarFiltros = () => {
    setFechaDesde(null);
    setFechaHasta(null);
    setTimeout(() => {
      loadDashboardData();
    }, 100);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD/MM');
  };

  const formatMonth = (dateString) => {
    return dayjs(dateString).format('MMM YY');
  };

  // Colores para gráficos
  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

  // Datos para gráfico de créditos por estado (torta)
  const getCreditosPorEstadoData = () => {
    if (!dashboardData?.graficos?.creditosPorEstado) return [];
    
    const estadosMap = {
      'activos': 'Activos',
      'usados': 'Usados',
      'vencidos': 'Vencidos',
      'proximos_vencer': 'Próx. Vencer'
    };
    
    return dashboardData.graficos.creditosPorEstado.map(item => ({
      name: estadosMap[item.estado] || item.estado,
      value: item.cantidad,
      estado: item.estado
    }));
  };

  // Datos para gráfico de ingresos por mes (barras)
  const getIngresosPorMesData = () => {
    if (!dashboardData?.graficos?.ingresosPorMes) return [];
    return [...dashboardData.graficos.ingresosPorMes]
      .reverse()
      .map(item => ({
        mes: formatMonth(item.mes),
        total: item.total,
        cantidadPagos: item.cantidadPagos
      }));
  };

  // Datos para gráfico de créditos por día (líneas)
  const getCreditosPorDiaData = () => {
    if (!dashboardData?.graficos?.creditosPorDia) return [];
    return [...dashboardData.graficos.creditosPorDia]
      .reverse()
      .slice(-15) // Mostrar solo últimos 15 días en móvil
      .map(item => ({
        dia: formatDate(item.dia),
        activos: item.activos,
        usados: item.usados,
        total: item.total
      }));
  };

  // Datos para gráfico de ingresos por día (líneas)
  const getIngresosPorDiaData = () => {
    if (!dashboardData?.graficos?.ingresosPorDia) return [];
    return [...dashboardData.graficos.ingresosPorDia]
      .reverse()
      .slice(-15) // Mostrar solo últimos 15 días
      .map(item => ({
        dia: formatDate(item.dia),
        total: item.total,
        cantidadPagos: item.cantidadPagos
      }));
  };

  // Columnas responsive para tablas
  const columnsTopUsados = isMobile ? [
    {
      title: 'Alumno',
      dataIndex: 'alumno',
      key: 'alumno',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.alu_nombre} {record.alu_apellido}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.alu_email}</div>
        </div>
      ),
    },
    {
      title: 'Usados',
      dataIndex: 'creditos_usados',
      key: 'creditos_usados',
      align: 'center',
      width: 70,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
  ] : [
    {
      title: 'Alumno',
      dataIndex: 'alumno',
      key: 'alumno',
      render: (_, record) => `${record.alu_nombre} ${record.alu_apellido}`,
    },
    {
      title: 'Email',
      dataIndex: 'alu_email',
      key: 'alu_email',
    },
    {
      title: 'Créditos Usados',
      dataIndex: 'creditos_usados',
      key: 'creditos_usados',
      align: 'center',
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
  ];

  const columnsTopComprados = isMobile ? [
    {
      title: 'Alumno',
      dataIndex: 'alumno',
      key: 'alumno',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.alu_nombre} {record.alu_apellido}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{formatCurrency(record.total_gastado)}</div>
        </div>
      ),
    },
    {
      title: 'Comprados',
      dataIndex: 'creditos_comprados',
      key: 'creditos_comprados',
      align: 'center',
      width: 80,
      render: (value) => <Tag color="green">{value}</Tag>,
    },
  ] : [
    {
      title: 'Alumno',
      dataIndex: 'alumno',
      key: 'alumno',
      render: (_, record) => `${record.alu_nombre} ${record.alu_apellido}`,
    },
    {
      title: 'Email',
      dataIndex: 'alu_email',
      key: 'alu_email',
    },
    {
      title: 'Créditos Comprados',
      dataIndex: 'creditos_comprados',
      key: 'creditos_comprados',
      align: 'center',
      render: (value) => <Tag color="green">{value}</Tag>,
    },
    {
      title: 'Total Gastado',
      dataIndex: 'total_gastado',
      key: 'total_gastado',
      align: 'right',
      render: (value) => formatCurrency(value),
    },
  ];

  if (loading && !dashboardData) {
    return (
      <MainLayout>
        <div style={{ 
          padding: '50px', 
          textAlign: 'center',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!dashboardData) {
    return (
      <MainLayout>
        <div style={{ padding: '20px' }}>
          <Card>
            <p>No se pudieron cargar los datos del dashboard</p>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const { creditos, ingresos, pagos, alumnos, topAlumnos } = dashboardData;

  // Componente de filtros de fecha - Responsive
  const FiltrosFecha = () => (
    <Card 
      style={{ marginBottom: '16px' }}
      bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
    >
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: '12px',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <RangePicker
          value={fechaDesde && fechaHasta ? [fechaDesde, fechaHasta] : null}
          onChange={handleFechaChange}
          format="DD/MM/YYYY"
          placeholder={['Desde', 'Hasta']}
          style={{ width: '100%', flex: isMobile ? undefined : 1, maxWidth: isMobile ? undefined : '300px' }}
          size={isMobile ? 'middle' : 'middle'}
        />
        <Space.Compact style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button 
            type="primary" 
            icon={<FilterOutlined />}
            onClick={handleAplicarFiltros}
            style={{ flex: isMobile ? 1 : undefined }}
          >
            {isMobile ? 'Filtrar' : 'Aplicar'}
          </Button>
          <Button 
            icon={<ClearOutlined />}
            onClick={handleLimpiarFiltros}
            style={{ flex: isMobile ? 1 : undefined }}
          >
            Limpiar
          </Button>
        </Space.Compact>
      </div>
    </Card>
  );

  // Componente de tarjeta métrica pequeña para móvil
  const MetricCard = ({ title, value, prefix, valueStyle, suffix }) => (
    <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
      <Statistic
        title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>{title}</span>}
        value={value}
        prefix={prefix}
        suffix={suffix}
        valueStyle={{ 
          ...valueStyle, 
          fontSize: isMobile ? '20px' : '24px' 
        }}
      />
    </Card>
  );

  // Pestaña 1: Resumen General
  const TabResumen = () => (
    <div>
      <FiltrosFecha />
      
      {/* Créditos - Grid 2x2 en móvil */}
      <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
        <Col xs={12} sm={12} lg={6}>
          <MetricCard
            title="Créditos Comprados"
            value={creditos.comprados}
            prefix={<CreditCardOutlined />}
            valueStyle={{ color: '#3f8600' }}
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <MetricCard
            title="Créditos Usados"
            value={creditos.usados}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <MetricCard
            title="Créditos Activos"
            value={creditos.activos}
            prefix={<CreditCardOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <MetricCard
            title="Créditos Vencidos"
            value={creditos.vencidos}
            prefix={<WarningOutlined />}
            valueStyle={{ color: '#cf1322' }}
          />
        </Col>
      </Row>

      {/* Ingresos */}
      <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} lg={8}>
          <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Ingresos Totales</span>}
              value={ingresos.total}
              prefix={<DollarOutlined />}
              formatter={(value) => isMobile ? formatCurrencyShort(value) : formatCurrency(value)}
              valueStyle={{ color: '#3f8600', fontSize: isMobile ? '20px' : '24px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={8}>
          <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Ingresos del Mes</span>}
              value={ingresos.mesActual}
              prefix={<DollarOutlined />}
              formatter={(value) => isMobile ? formatCurrencyShort(value) : formatCurrency(value)}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? '20px' : '24px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={8}>
          <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Pagos Pendientes</span>}
              value={pagos.pendientes.cantidad}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14', fontSize: isMobile ? '20px' : '24px' }}
            />
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
              {formatCurrency(pagos.pendientes.montoTotal)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Alumnos */}
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={12} lg={12}>
          <MetricCard
            title="Alumnos Activos"
            value={alumnos.activos}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col xs={12} sm={12} lg={12}>
          <MetricCard
            title="Alumnos Inactivos"
            value={alumnos.inactivos}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#8c8c8c' }}
          />
        </Col>
      </Row>

      {/* Alerta de créditos próximos a vencer */}
      {creditos.proximosVencer > 0 && (
        <Row gutter={[12, 12]} style={{ marginTop: '16px' }}>
          <Col xs={24}>
            <Card
              bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
              style={{
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                border: 'none',
              }}
            >
              <Statistic
                title={
                  <span style={{ color: '#fff', fontSize: isMobile ? '12px' : '14px' }}>
                    ⚠️ Créditos Próximos a Vencer (7 días)
                  </span>
                }
                value={creditos.proximosVencer}
                valueStyle={{ color: '#fff', fontSize: isMobile ? '28px' : '32px' }}
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );

  // Pestaña 2: Créditos
  const TabCreditos = () => (
    <div>
      <FiltrosFecha />
      
      {/* Gráficos - Full width en móvil */}
      <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Distribución por Estado</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            {getCreditosPorEstadoData().length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <PieChart>
                  <Pie
                    data={getCreditosPorEstadoData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={isMobile ? 60 : 80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getCreditosPorEstadoData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend 
                    layout={isMobile ? 'horizontal' : 'vertical'}
                    align={isMobile ? 'center' : 'right'}
                    verticalAlign={isMobile ? 'bottom' : 'middle'}
                    wrapperStyle={{ fontSize: isMobile ? '12px' : '14px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ textAlign: 'center', color: '#888' }}>No hay datos disponibles</p>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Créditos por Día</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            {getCreditosPorDiaData().length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <LineChart data={getCreditosPorDiaData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="dia" 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    interval={isMobile ? 2 : 0}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} width={30} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: isMobile ? '12px' : '14px' }} />
                  <Line type="monotone" dataKey="total" stroke="#faad14" name="Total" strokeWidth={2} dot={!isMobile} />
                  <Line type="monotone" dataKey="usados" stroke="#1890ff" name="Usados" strokeWidth={2} dot={!isMobile} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ textAlign: 'center', color: '#888' }}>No hay datos disponibles</p>
            )}
          </Card>
        </Col>
      </Row>

      {/* Tablas */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Top Alumnos - Créditos Usados</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            <Table
              columns={columnsTopUsados}
              dataSource={topAlumnos.creditosUsados?.slice(0, isMobile ? 5 : 10)}
              rowKey="alu_id"
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 280 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Top Alumnos - Créditos Comprados</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            <Table
              columns={columnsTopComprados}
              dataSource={topAlumnos.creditosComprados?.slice(0, isMobile ? 5 : 10)}
              rowKey="alu_id"
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 280 : undefined }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Pestaña 3: Ingresos
  const TabIngresos = () => (
    <div>
      <FiltrosFecha />
      
      {/* Gráfico de barras - Ingresos por mes */}
      <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Ingresos por Mes</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            {getIngresosPorMesData().length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                <BarChart data={getIngresosPorMesData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis 
                    tick={{ fontSize: isMobile ? 10 : 12 }} 
                    width={isMobile ? 40 : 60}
                    tickFormatter={(value) => isMobile ? formatCurrencyShort(value) : formatCurrency(value)}
                  />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? '12px' : '14px' }} />
                  <Bar dataKey="total" fill="#52c41a" name="Ingresos" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ textAlign: 'center', color: '#888' }}>No hay datos disponibles</p>
            )}
          </Card>
        </Col>
      </Row>

      {/* Gráfico de línea - Ingresos por día */}
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Ingresos por Día</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            {getIngresosPorDiaData().length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                <LineChart data={getIngresosPorDiaData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="dia" 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    interval={isMobile ? 2 : 0}
                  />
                  <YAxis 
                    tick={{ fontSize: isMobile ? 10 : 12 }} 
                    width={isMobile ? 40 : 60}
                    tickFormatter={(value) => isMobile ? formatCurrencyShort(value) : formatCurrency(value)}
                  />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? '12px' : '14px' }} />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#1890ff" 
                    name="Ingresos" 
                    strokeWidth={2}
                    dot={!isMobile}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ textAlign: 'center', color: '#888' }}>No hay datos disponibles</p>
            )}
          </Card>
        </Col>
      </Row>

      {/* Tabla detalle */}
      <Row gutter={[12, 12]} style={{ marginTop: '16px' }}>
        <Col xs={24}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Detalle de Ingresos por Mes</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            <Table
              columns={[
                {
                  title: 'Mes',
                  dataIndex: 'mes',
                  key: 'mes',
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  align: 'right',
                  render: (value) => formatCurrency(value),
                },
                {
                  title: isMobile ? 'Pagos' : 'Cantidad de Pagos',
                  dataIndex: 'cantidadPagos',
                  key: 'cantidadPagos',
                  align: 'center',
                },
              ]}
              dataSource={getIngresosPorMesData().map((item, index) => ({
                ...item,
                key: index
              }))}
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 280 : undefined }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Pestaña 4: Alumnos
  const TabAlumnos = () => (
    <div>
      <FiltrosFecha />
      
      {/* Métricas de alumnos */}
      <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={8} lg={8}>
          <MetricCard
            title="Total de Alumnos"
            value={alumnos.activos + alumnos.inactivos}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col xs={12} sm={8} lg={8}>
          <MetricCard
            title="Activos"
            value={alumnos.activos}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col xs={12} sm={8} lg={8}>
          <MetricCard
            title="Inactivos"
            value={alumnos.inactivos}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#8c8c8c' }}
          />
        </Col>
      </Row>

      {/* Tablas de rankings */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Top Alumnos - Créditos Usados</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            <Table
              columns={columnsTopUsados}
              dataSource={topAlumnos.creditosUsados?.slice(0, isMobile ? 5 : 10)}
              rowKey="alu_id"
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 280 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>Top Alumnos - Créditos Comprados</span>}
            bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
          >
            <Table
              columns={columnsTopComprados}
              dataSource={topAlumnos.creditosComprados?.slice(0, isMobile ? 5 : 10)}
              rowKey="alu_id"
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 280 : undefined }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  const tabItems = [
    {
      key: 'resumen',
      label: isMobile ? 'Resumen' : 'Resumen General',
      children: <TabResumen />,
    },
    {
      key: 'creditos',
      label: 'Créditos',
      children: <TabCreditos />,
    },
    {
      key: 'ingresos',
      label: 'Ingresos',
      children: <TabIngresos />,
    },
    {
      key: 'alumnos',
      label: 'Alumnos',
      children: <TabAlumnos />,
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '12px' : '24px' }}>
        <Title 
          level={isMobile ? 4 : 2} 
          style={{ 
            marginBottom: isMobile ? '16px' : '24px', 
            color: '#e0e0e0' 
          }}
        >
          Dashboard
        </Title>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size={isMobile ? 'small' : 'large'}
          tabBarStyle={{ marginBottom: isMobile ? '12px' : '24px' }}
        />
      </div>
    </MainLayout>
  );
};

export default Dashboard;
